import { useEffect } from 'react';
import { useSavedCallback } from '.';
import { isFunction } from 'lodash';

/**
 * Custom hook to make actions cancellable. The action is cancelled when the
 * component using this hook is unmounted or the action changes.
 *
 * The onSucces and onError callbacks are only called if the action is not
 * canceled.
 *
 * @param action the action: a promise, a plain value, or a function returning
 *   either; `null`/`undefined` means "no action".
 * @param onSuccess callback to call when the action completes successfully
 * @param onError callback to call when the action produces an error
 * @param onCanceled callback to call when the action is canceled.
 */
export function useCancelable(
  action: unknown,
  onSuccess?: (...results: unknown[]) => void,
  onError?: (...errors: unknown[]) => void,
  onCanceled?: () => void
): void {
  const savedOnSuccess = useSavedCallback(onSuccess);
  const savedOnError = useSavedCallback(onError);
  const savedOnCanceled = useSavedCallback(onCanceled);

  useEffect(() => {
    let canceled = false;

    if (action != null) {
      Promise.resolve(isFunction(action) ? action() : action)
        .then((...results) => {
          if (!canceled) {
            if (savedOnSuccess.current != null) savedOnSuccess.current(...results);
          } else {
            if (savedOnCanceled.current != null) savedOnCanceled.current();
          }
        })
        .catch((...errors) => {
          if (!canceled) {
            if (savedOnError.current != null) savedOnError.current(...errors);
          } else {
            if (savedOnCanceled.current != null) savedOnCanceled.current();
          }
        });
    }

    return () => {
      canceled = true;
    };
  }, [action]);
}
